import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { X, Camera as CameraIcon, GripVertical, Maximize2, Minimize2, ExternalLink } from 'lucide-react';
import { ASPECT_RATIOS } from '../Editor/ProjectionSystem';

/**
 * Projector POV — floating live preview window.
 *
 * Features:
 *   • Aspect-locked sizing (driven by the projector's aspect_ratio field).
 *     Default 16:10. When the projector's aspect changes, the window resizes
 *     automatically. The user CANNOT stretch — only one slider (width) drives
 *     height = width / aspect.
 *   • Pop-out to a real OS window (multi-monitor): user drags the popup to a
 *     second display. Same RTT, pixels are pushed into the popup's <canvas>.
 *   • True fullscreen: hides all chrome, shows ONLY the canvas edge-to-edge.
 *     Works on whichever monitor the popup currently lives on.
 *   • Visibility-aware pause: when popup is hidden / tab hidden, the underlying
 *     Babylon RTT stops rendering → zero cost while not visible.
 *   • Live frame loop: ~30fps preview (Babylon RTT runs every 2 main frames).
 *
 * No mutation of: main camera, projection / shadow / LED / truss / scaffolding.
 */

const HEADER_H = 32;
const MIN_W = 240;
const MIN_H = 135;
// Hard cap matches the RTT cap inside BabylonScene so we never ask for a size
// the engine will silently downscale. Keeps display 1:1 with render texture.
const MAX_RTT_W = 1280;
const MAX_RTT_H = 720;

const parseAspect = (str) => {
  // Prefer the canonical lookup table; fall back to 16:10 default.
  const fromMap = ASPECT_RATIOS?.[str]?.value;
  if (typeof fromMap === 'number' && isFinite(fromMap)) return fromMap;
  if (typeof str === 'string' && str.includes(':')) {
    const [a, b] = str.split(':').map(Number);
    if (a > 0 && b > 0) return a / b;
  }
  return 16 / 10;
};

const ProjectorPOVWindow = ({ projectorId, projectorName, projectorAspect, initialX, initialY, initialW, onClose }) => {
  const aspect = useMemo(() => parseAspect(projectorAspect), [projectorAspect]);

  // Initial size: width chosen by parent, height derived from aspect + header.
  const initWidth = initialW || 480;
  const computeAspectSize = useCallback((w) => {
    const cw = Math.min(MAX_RTT_W, Math.max(MIN_W, Math.round(w)));
    const ch = Math.round(cw / aspect);
    const clampedCh = Math.min(MAX_RTT_H, Math.max(MIN_H, ch));
    // If clamping ch reduced height, recompute width to preserve aspect
    const finalCw = clampedCh < ch ? Math.round(clampedCh * aspect) : cw;
    return { w: finalCw, h: clampedCh };
  }, [aspect]);

  const [pos, setPos] = useState({ x: initialX || 80, y: initialY || 100 });
  const [{ w: cvW, h: cvH }, setCanvasSize] = useState(() => computeAspectSize(initWidth));
  const [opacity, setOpacity] = useState(1.0);
  const [poppedOut, setPoppedOut] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const inAppCanvasRef = useRef(null);
  const popupCanvasRef = useRef(null);
  const popupRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(0);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  // ── Re-apply aspect when projector aspect changes externally ─────────────
  useEffect(() => {
    setCanvasSize(prev => computeAspectSize(prev.w));
  }, [computeAspectSize]);

  // ── Push the latest RTT size to Babylon whenever cvW/cvH changes ─────────
  useEffect(() => {
    window.resizeProjectorPOVWindow?.(projectorId, cvW, cvH);
  }, [projectorId, cvW, cvH]);

  // ── DRAG (header) — only while in-app, not when popped out ───────────────
  const onHeaderMouseDown = (e) => {
    if (poppedOut || fullscreen) return;
    if (e.target.closest('[data-no-drag]')) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    document.body.style.userSelect = 'none';
    e.preventDefault();
  };

  // ── RESIZE (corner grip) — width-driven, height auto-locks to aspect ─────
  const onResizeMouseDown = (e) => {
    if (poppedOut || fullscreen) return;
    resizeRef.current = { startX: e.clientX, origW: cvW };
    document.body.style.userSelect = 'none';
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (dragRef.current) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        setPos({
          x: Math.max(0, Math.min(window.innerWidth - 80, dragRef.current.origX + dx)),
          y: Math.max(0, Math.min(window.innerHeight - HEADER_H, dragRef.current.origY + dy))
        });
      } else if (resizeRef.current) {
        const newW = resizeRef.current.origW + (e.clientX - resizeRef.current.startX);
        setCanvasSize(computeAspectSize(newW));
      }
    };
    const onUp = () => {
      dragRef.current = null;
      resizeRef.current = null;
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [computeAspectSize]);

  // ── LIVE FRAME LOOP — pull RTT pixels and blit to canvas (in-app + popup) ─
  // Single RAF is shared across both targets. Babylon RTT already throttled
  // to 30fps via refreshRate=2, so this RAF only does meaningful work on
  // alternate frames; cheap.
  useEffect(() => {
    let cancelled = false;
    let busy = false;

    const drawTo = (cv, pixels, width, height) => {
      if (!cv) return;
      if (cv.width !== width || cv.height !== height) {
        cv.width = width;
        cv.height = height;
      }
      const ctx = cv.getContext('2d');
      const img = ctx.createImageData(width, height);
      const stride = width * 4;
      for (let y = 0; y < height; y++) {
        const src = (height - 1 - y) * stride;
        const dst = y * stride;
        img.data.set(pixels.subarray(src, src + stride), dst);
      }
      ctx.putImageData(img, 0, 0);
    };

    const tick = async () => {
      if (cancelled) return;
      if (!busy && typeof window.readProjectorPOVPixels === 'function') {
        busy = true;
        try {
          const result = await window.readProjectorPOVPixels(projectorId);
          if (!cancelled && result) {
            const { pixels, width, height } = result;
            if (inAppCanvasRef.current) drawTo(inAppCanvasRef.current, pixels, width, height);
            if (popupCanvasRef.current) drawTo(popupCanvasRef.current, pixels, width, height);
          }
        } catch (e) { /* ignore */ }
        finally { busy = false; }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
    };
  }, [projectorId]);

  // ── POP-OUT to a real OS window (multi-monitor support) ──────────────────
  const handlePopOut = useCallback(() => {
    if (poppedOut) {
      try { popupRef.current?.close(); } catch (e) {}
      return;
    }
    // Try the Window Management API to land on a secondary screen if available
    let popupW = cvW, popupH = cvH + HEADER_H;
    let leftPos = window.screenX + 100;
    let topPos = window.screenY + 100;
    const features = `popup,width=${popupW},height=${popupH},left=${leftPos},top=${topPos}`;
    const popup = window.open('', `pov_${projectorId}`, features);
    if (!popup) {
      alert('Pop-out blocked. Please allow popups for this site to use multi-display preview.');
      return;
    }
    popupRef.current = popup;
    popup.document.title = `POV: ${projectorName || projectorId}`;
    popup.document.body.style.margin = '0';
    popup.document.body.style.background = '#000';
    popup.document.body.style.overflow = 'hidden';
    popup.document.body.style.cursor = 'default';
    popup.document.body.innerHTML = `
      <style>
        :root { color-scheme: dark; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        .pov-bar { position: fixed; top: 0; left: 0; right: 0; height: 28px; display: flex;
                   align-items: center; gap: 8px; padding: 0 10px; background: rgba(8,12,18,0.85);
                   color: #d4d4d8; font-size: 11px; letter-spacing: 0.04em; z-index: 5;
                   border-bottom: 1px solid rgba(34,211,238,0.25); user-select: none;
                   transition: opacity 0.25s; }
        .pov-bar.hidden { opacity: 0; pointer-events: none; }
        .pov-bar b { color: #67e8f9; font-weight: 600; }
        .pov-btn { margin-left: auto; background: transparent; color: #d4d4d8; border: 1px solid #3f3f46;
                   border-radius: 4px; padding: 2px 8px; font-size: 11px; cursor: pointer; }
        .pov-btn:hover { background: rgba(34,211,238,0.15); border-color: #22d3ee; color: #fff; }
        .pov-stage { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; background: #000; }
        .pov-canvas { display: block; max-width: 100%; max-height: 100%; }
        .pov-canvas.fs { width: 100vw; height: 100vh; object-fit: contain; }
      </style>
      <div class="pov-bar" id="pov-bar">
        <span>📷 POV: <b>${projectorName || projectorId}</b></span>
        <span style="color:#71717a">${cvW}×${cvH} · live · double-click for fullscreen</span>
        <button class="pov-btn" id="pov-fs-btn">Fullscreen</button>
      </div>
      <div class="pov-stage" id="pov-stage">
        <canvas class="pov-canvas" id="pov-canvas"></canvas>
      </div>
    `;
    const popupCanvas = popup.document.getElementById('pov-canvas');
    popupCanvasRef.current = popupCanvas;
    setPoppedOut(true);

    // Auto-hide titlebar after 3s of no movement (cleaner monitoring view)
    const bar = popup.document.getElementById('pov-bar');
    let hideTo;
    const showBar = () => {
      bar.classList.remove('hidden');
      clearTimeout(hideTo);
      hideTo = setTimeout(() => bar.classList.add('hidden'), 3000);
    };
    showBar();
    popup.document.addEventListener('mousemove', showBar);

    // Fullscreen on the popup itself (so it goes fullscreen on whichever
    // monitor the user has dragged the popup to)
    const enterFs = async () => {
      try {
        await popup.document.documentElement.requestFullscreen();
        popupCanvas.classList.add('fs');
      } catch (e) { /* user cancelled */ }
    };
    popup.document.getElementById('pov-fs-btn').addEventListener('click', enterFs);
    popup.document.addEventListener('dblclick', enterFs);
    popup.document.addEventListener('fullscreenchange', () => {
      if (!popup.document.fullscreenElement) popupCanvas.classList.remove('fs');
    });

    // Pause RTT when popup is hidden (minimized / occluded)
    const onVis = () => window.pauseProjectorPOVWindow?.(projectorId, popup.document.hidden);
    popup.document.addEventListener('visibilitychange', onVis);

    // If popup is closed by the user, treat as window-close
    const closeWatcher = setInterval(() => {
      if (popup.closed) {
        clearInterval(closeWatcher);
        popupCanvasRef.current = null;
        popupRef.current = null;
        setPoppedOut(false);
        // Resume the in-app preview if user closed only the popup
        window.pauseProjectorPOVWindow?.(projectorId, false);
      }
    }, 500);
  }, [projectorId, projectorName, cvW, cvH, poppedOut]);

  // Clean up popup on unmount
  useEffect(() => {
    return () => { try { popupRef.current?.close(); } catch (e) {} };
  }, []);

  // ── FULLSCREEN (in-app version) ──────────────────────────────────────────
  const handleFullscreen = useCallback(async () => {
    if (poppedOut) return; // popup has its own fullscreen button
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      try { await el.requestFullscreen(); } catch (e) {}
    }
  }, [poppedOut]);

  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // ── Pause RTT when main tab hidden (zero cost when not visible) ─────────
  useEffect(() => {
    const onVis = () => {
      if (!poppedOut) window.pauseProjectorPOVWindow?.(projectorId, document.hidden);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [projectorId, poppedOut]);

  const handleClose = useCallback(() => {
    try { popupRef.current?.close(); } catch (e) {}
    window.closeProjectorPOVWindow?.(projectorId);
    onClose?.(projectorId);
  }, [projectorId, onClose]);

  // Snap to corners
  const snap = (corner) => {
    const w = cvW;
    const h = cvH + HEADER_H;
    const margin = 16;
    const positions = {
      tl: { x: margin, y: 80 },
      tr: { x: window.innerWidth - w - margin, y: 80 },
      bl: { x: margin, y: window.innerHeight - h - margin },
      br: { x: window.innerWidth - w - margin, y: window.innerHeight - h - margin }
    };
    setPos(positions[corner] || positions.tl);
  };

  // ── In-app rendering — hidden when popped out (popup owns the canvas) ────
  if (poppedOut) {
    // Render only a small "popped out" stub so the user can re-dock if needed
    return (
      <div
        className="fixed z-[55] flex items-center gap-2 px-3 py-2 rounded-full border border-cyan-400/40 bg-zinc-950/85 backdrop-blur shadow-lg shadow-cyan-500/20"
        style={{ left: pos.x, top: pos.y }}
        data-testid={`projector-pov-window-${projectorId}`}
      >
        <ExternalLink className="w-3.5 h-3.5 text-cyan-300" />
        <span className="text-[11px] text-zinc-200">
          POV: <b className="text-cyan-300">{projectorName}</b> opened in popup
        </span>
        <button
          onClick={handlePopOut}
          className="text-[10px] text-zinc-300 hover:text-white border border-zinc-700 rounded px-2 py-0.5"
          data-testid={`pov-redock-${projectorId}`}
        >
          Re-dock
        </button>
        <button
          onClick={handleClose}
          className="text-zinc-300 hover:text-red-400"
          data-testid={`pov-close-${projectorId}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${fullscreen ? 'fixed inset-0 z-[80] bg-black' : 'fixed z-[55] rounded-lg border border-cyan-400/40 bg-zinc-950/90 backdrop-blur shadow-2xl shadow-cyan-500/20'} select-none`}
      style={fullscreen ? {} : { left: pos.x, top: pos.y, width: cvW, height: cvH + HEADER_H, opacity }}
      data-testid={`projector-pov-window-${projectorId}`}
    >
      {/* Header (hidden in fullscreen) */}
      {!fullscreen && (
        <div
          className="flex items-center gap-2 px-2 cursor-move border-b border-cyan-400/20 bg-gradient-to-r from-cyan-950/60 to-fuchsia-950/40"
          style={{ height: HEADER_H }}
          onMouseDown={onHeaderMouseDown}
          onDoubleClick={handleFullscreen}
        >
          <GripVertical className="w-3 h-3 text-zinc-500 shrink-0" />
          <CameraIcon className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
          <span className="text-[11px] text-zinc-200 truncate">
            <span className="text-zinc-500">POV:</span>{' '}
            <span className="text-cyan-300 font-semibold">{projectorName || projectorId}</span>
            <span className="text-zinc-500 ml-1.5 hidden sm:inline">
              {projectorAspect || '16:10'} · {cvW}×{cvH}
            </span>
          </span>

          <div className="ml-auto flex items-center gap-1" data-no-drag>
            {/* Snap to corners */}
            <div className="hidden sm:flex items-center gap-0.5 mr-1">
              <button onClick={() => snap('tl')} title="Snap top-left"
                className="w-3.5 h-3.5 rounded-sm border border-zinc-600 hover:border-cyan-400" />
              <button onClick={() => snap('tr')} title="Snap top-right"
                className="w-3.5 h-3.5 rounded-sm border border-zinc-600 hover:border-cyan-400" />
              <button onClick={() => snap('bl')} title="Snap bottom-left"
                className="w-3.5 h-3.5 rounded-sm border border-zinc-600 hover:border-cyan-400" />
              <button onClick={() => snap('br')} title="Snap bottom-right"
                className="w-3.5 h-3.5 rounded-sm border border-zinc-600 hover:border-cyan-400" />
            </div>

            {/* Opacity */}
            <input
              type="range" min="0.3" max="1" step="0.05"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              className="w-12 h-1 cursor-pointer accent-cyan-400"
              title="Opacity"
              data-testid={`pov-opacity-${projectorId}`}
            />

            {/* Pop-out (multi-monitor) */}
            <button
              onClick={handlePopOut}
              className="p-0.5 rounded hover:bg-cyan-500/20 hover:text-cyan-300 text-zinc-300"
              title="Pop out to new window (drag to second monitor)"
              data-testid={`pov-popout-${projectorId}`}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </button>

            {/* Fullscreen */}
            <button
              onClick={handleFullscreen}
              className="p-0.5 rounded hover:bg-cyan-500/20 hover:text-cyan-300 text-zinc-300"
              title="Fullscreen (ESC to exit)"
              data-testid={`pov-fullscreen-${projectorId}`}
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>

            {/* Close */}
            <button
              onClick={handleClose}
              className="p-0.5 rounded hover:bg-red-500/20 hover:text-red-300 text-zinc-300"
              title="Close"
              data-testid={`pov-close-${projectorId}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Live preview canvas */}
      <div
        className="relative bg-black overflow-hidden flex items-center justify-center"
        style={fullscreen ? { height: '100vh' } : { height: cvH }}
        onDoubleClick={handleFullscreen}
      >
        <canvas
          ref={inAppCanvasRef}
          className={fullscreen ? 'max-w-full max-h-full' : 'block w-full h-full'}
          style={fullscreen ? { aspectRatio: aspect, height: '100vh', width: 'auto' } : {}}
          data-testid={`pov-canvas-${projectorId}`}
        />

        {/* Bottom-right resize grip (hidden in fullscreen) */}
        {!fullscreen && (
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-ew-resize"
            onMouseDown={onResizeMouseDown}
            title="Resize (aspect-locked)"
            style={{ background: 'linear-gradient(135deg, transparent 50%, rgba(34, 211, 238, 0.6) 50%)' }}
            data-testid={`pov-resize-${projectorId}`}
          />
        )}

        {/* Live label overlay (hidden in fullscreen) */}
        {!fullscreen && (
          <div className="absolute top-1 left-2 text-[9px] tracking-[0.2em] uppercase text-cyan-300/70 pointer-events-none">
            Live · 30fps · {projectorAspect || '16:10'}
          </div>
        )}

        {/* Fullscreen exit hint (1.5s autohide) */}
        {fullscreen && (
          <button
            onClick={handleFullscreen}
            className="absolute top-3 right-3 z-10 px-2 py-1 text-[10px] uppercase tracking-wider rounded border border-zinc-600/50 bg-black/40 text-zinc-300 hover:text-white opacity-30 hover:opacity-100 transition-opacity"
            data-testid={`pov-exit-fullscreen-${projectorId}`}
          >
            <Minimize2 className="w-3 h-3 inline mr-1" /> Exit fullscreen
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectorPOVWindow;
